<template>
  <article class="page-lab" :lang="lang === 'zh' ? 'zh-Hant' : 'en'">
    <header class="lab-heading">
      <p class="lab-eyebrow">CHE-YU WU / STRATA 地層</p>
      <!-- 白話副標：名字抽象，副標先說這是什麼（哲宇 2026-10-03）。年份範圍從索引算，不寫死。 -->
      <p class="lab-tagline">{{ t.tagline }}</p>
      <h1>{{ heading.title }}</h1>
      <div>
        <p>{{ heading.intro }}</p>
        <span
          >{{ catalog.period.start }} — {{ catalog.period.end }}<br />{{
            catalog.items.length
          }}
          {{ t.records }}</span
        >
      </div>
      <!-- The years, as the tabs on a row of case folders. One file in
           src/data/lab/ is one tab and one route: nothing here counts them. -->
      <nav class="lab-years" :aria-label="t.years">
        <RouterLink
          v-for="entry in years"
          :key="entry.year"
          :to="yearPath(entry)"
          class="lab-year-tab"
          :class="{ current: entry.year === activeYear }"
          :aria-current="entry.year === activeYear ? 'page' : undefined"
          >{{ tabText(entry) }}</RouterLink
        >
      </nav>
    </header>
    <div class="lab-reading-links">
      <p class="lab-legend">{{ t.legend }}</p>
      <a href="#lab-records">{{ t.index }} ↓</a>
    </div>
    <div class="lab-stage" :class="{ 'has-selection': selected }">
      <LabWall
        :items="visibleItems"
        :lang="lang"
        :selected="selected?.id"
        :period="catalog.period"
        :count="catalog.items.length"
        :year="activeYear"
        @select="select"
      />
      <section
        v-if="selected"
        class="lab-selected"
        tabindex="-1"
        aria-live="polite"
        aria-atomic="false"
      >
        <div>
          <button
            type="button"
            class="lab-select-record"
            @click="clearSelection"
          >
            {{ t.close }}
          </button>
          <p class="lab-eyebrow">{{ t.selected }}</p>
          <h2>{{ localText(selected.title, lang) }}</h2>
          <p>{{ localText(selected.summary, lang) }}</p>
          <a
            :href="sourceUrl(selected.url)"
            :target="external(selected.url) ? '_blank' : undefined"
            rel="noopener noreferrer"
            @click="trackSource(selected, selected.url, 'primary')"
            >{{ t.open }} ↗︎</a
          >
        </div>
        <aside>
          <p>
            {{ selected.date || t.undated }} ·
            {{ localText(selected.dateNote, lang) }}
          </p>
          <p>{{ statusText(selected.status) }}</p>
          <ul v-if="connections.length">
            <li v-for="edge in connections" :key="edge.id">
              <button type="button" @click="select(edge.id, false, 'connection')">
                {{ localText(itemById(edge.id).title, lang) }}</button
              ><span
                >{{ localText(edge.label, lang) }} ·
                {{ edge.type === "theme" ? t.theme : t.sourceRelation }}</span
              >
            </li>
          </ul>
        </aside>
      </section>
    </div>
    <!-- 創作族譜的入口放在板子與逐一閱讀之間（哲宇 9/13）：板子是「這一年發生了什麼」，族譜是「這些草圖怎麼一件長出一件」 -->
    <lineage-teaser class="lab-lineage-teaser" :zh="lang === 'zh'" />
    <!-- The dossier (DESIGN §十一之九): the wall read backwards, one page per
         record, newest first, the evidence pinned on the left and the typed
         report on the right. Each <article id="lab-{id}"> is the anchor the
         AI endpoints cite, and their order is a contract kept by
         scripts/test-lab-content.mjs: labNewestFirst and nothing else. -->
    <section id="lab-records" class="lab-records">
      <header>
        <p class="lab-eyebrow">{{ t.index }}</p>
        <h2>{{ t.readTitle }}</h2>
        <p>{{ t.readNote }}</p>
      </header>
      <template v-for="{ item, yearTab } in records" :key="item.id">
        <p v-if="yearTab" class="lab-year-mark" aria-hidden="true">
          {{ yearTab }}
        </p>
        <article
          :id="'lab-' + item.id"
          class="lab-record"
          :class="{ selected: selected?.id === item.id }"
        >
          <!-- The paper: the same ten shapes as the wall, decided by the
               record (lab-model.mjs labPaperShape). A photograph is shown
               whole, at its own proportions, as wide as the column and as
               tall as it needs; the wall may crop a year's pictures to fit
               the board, the dossier never does. -->
          <figure
            class="lab-record-paper"
            :class="[
              'shape-' + paperShape(item),
              'fasten-' + fasten(item),
              { 'has-image': !!image(item) },
            ]"
            :style="{ '--tilt': tilt(item) + 'deg', '--pin': pinTone(item) }"
          >
            <b
              v-if="paperShape(item) === 'clipping'"
              class="lab-record-masthead"
              aria-hidden="true"
              >{{ masthead(item) }}</b
            >
            <img
              v-if="image(item)"
              :src="image(item)"
              :alt="
                localText(item.image.alt, lang) || localText(item.title, lang)
              "
              loading="lazy"
              decoding="async"
            />
            <!-- A paper with no picture prints what it is: its headline and
                 the start of the record. Drawn from data attributes, so the
                 page text and the AI extract carry the record once, in the
                 report beside it. -->
            <span
              v-if="!image(item)"
              class="lab-record-sheet"
              aria-hidden="true"
              :data-title="localText(item.title, lang)"
              :data-lede="localText(item.summary, lang)"
            ></span>
            <figcaption>
              <time v-if="item.date" :datetime="item.date">{{ item.date }}</time
              ><span v-else>{{ t.undated }}</span
              >{{ " " }}<span class="lab-record-kind">{{ kindText(item.kind) }}</span
              ><small v-if="item.image?.credit">{{
                localText(item.image.credit, lang)
              }}</small>
            </figcaption>
            <!-- Fastenings are <span>, not <i>: the AI Markdown extractor
                 reads <i> as emphasis and would print an empty ** for each. -->
            <template v-if="fasten(item) === 'tape'"
              ><span class="lab-record-tape left" aria-hidden="true"></span
              ><span class="lab-record-tape right" aria-hidden="true"></span
            ></template>
            <span v-else class="lab-record-pin" aria-hidden="true"></span>
            <span
              v-if="paperShape(item) === 'tag'"
              class="lab-record-eyelet"
              aria-hidden="true"
            ></span>
          </figure>
          <div class="lab-record-report">
            <p class="lab-record-meta">
              <time v-if="item.date" :datetime="item.date">{{ item.date }}</time
              ><span v-else>{{ t.undated }}</span>
              · {{ statusText(item.status) }}
              <small v-if="item.dateNote">{{
                localText(item.dateNote, lang)
              }}</small>
            </p>
            <h3>{{ localText(item.title, lang) }}</h3>
            <p>{{ localText(item.summary, lang) }}</p>
            <ul class="lab-keywords">
              <li v-for="(keyword, i) in item.keywords" :key="i">
                {{ localText(keyword, lang) }}
              </li>
            </ul>
            <!-- Sources in the open, no longer folded away: a record is on
                 the wall because it has a public page, and the dossier is
                 where that evidence is read. -->
            <dl class="lab-record-links">
              <dt>{{ t.sources }}</dt>
              <dd>
                <a
                  :href="sourceUrl(item.url)"
                  :target="external(item.url) ? '_blank' : undefined"
                  rel="noopener noreferrer"
                  @click="trackSource(item, item.url, 'primary')"
                  >{{ t.open }} ↗︎</a
                >
              </dd>
              <dd v-for="source in item.sources || []" :key="source.url">
                <a
                  :href="source.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="trackSource(item, source.url, 'source')"
                  >{{ localText(source.label, lang) }} ↗︎</a
                >
              </dd>
              <template v-if="relations(item.id).length || crossLinks(item).length">
                <dt>{{ t.related }}</dt>
                <dd v-for="edge in relations(item.id)" :key="edge.id">
                  <a :href="'#lab-' + edge.id"
                    >→ {{ localText(itemById(edge.id).title, lang) }}</a
                  >
                  · {{ localText(edge.label, lang) }}
                  <small
                    >({{
                      edge.type === "theme" ? t.theme : t.sourceRelation
                    }})</small
                  >
                </dd>
                <dd v-for="edge in crossLinks(item)" :key="edge.year + '/' + edge.id">
                  <a :href="crossHref(edge)"
                    >→ {{ crossTitle(edge) }}（{{ crossYear(edge) }}）</a
                  >
                  · {{ localText(edge.label, lang) }}
                  <small
                    >({{
                      edge.type === "theme" ? t.theme : t.sourceRelation
                    }})</small
                  >
                </dd>
              </template>
            </dl>
            <button
              type="button"
              class="lab-select-record"
              @click="select(item.id, true, 'index')"
            >
              {{ t.showWall }} ↗︎
            </button>
          </div>
        </article>
      </template>
    </section>
    <p v-if="!catalog.items.length" class="lab-empty">{{ t.empty }}</p>
    <site-links />
  </article>
</template>
<script setup>
import SiteLinks from '@/components/ui/SiteLinks.vue'
import { computed, ref, watch, nextTick, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import LabWall from "@/components/lab/LabWall.vue";
import labIndex from "@/data/lab/index.json";
import LineageTeaser from '@/components/lineage/LineageTeaser.vue'
import {
  localText,
  validateLabCatalog,
  labRelations,
  labCrossLinks,
  labGroup,
  labNewestFirst,
  labPaperShape,
  labFasten,
  labHash,
  LAB_GROUPS,
  LAB_PIN_TONES,
} from "@/runtime/lab-model.mjs";
import { trackEvent } from "@/runtime/analytics.mjs";
// Every year of the wall, loaded by name. The index is the list: adding
// src/data/lab/2025.json and one row to index.json adds a tab, a route, a
// sitemap entry and an AI record, and changes no code here.
const labFiles = import.meta.glob("../../data/lab/*.json", { eager: true }),
  archives = new Map(
    Object.entries(labFiles).map(([file, module]) => [
      file.slice(file.lastIndexOf("/") + 1),
      module.default || module,
    ]),
  ),
  years = [...labIndex].sort((a, b) => b.year.localeCompare(a.year));
for (const entry of years)
  if (!archives.has(entry.file))
    throw Error("Missing lab archive: " + entry.file);
const props = defineProps({
  catalog: { type: Object, default: null },
});
const route = useRoute(),
  router = useRouter(),
  lang = computed(() => (route.path.startsWith("/zh/") ? "zh" : "en")),
  group = ref("all");
// The bare route is the newest year, and a year outside the index never gets
// here: the router only knows the paths the index produced.
const activeYear = computed(() =>
    years.some((entry) => entry.year === route.params.year)
      ? String(route.params.year)
      : years[0].year,
  ),
  yearEntry = computed(() =>
    years.find((entry) => entry.year === activeYear.value),
  ),
  yearPath = (entry) =>
    (lang.value === "zh" ? "/zh" : "") +
    "/strata/" +
    (entry.year === years[0].year ? "" : entry.year + "/"),
  // A twelve-month wall's tab is the year it is named after and nothing else
  // (哲宇 9/18: 「簡化一下那個 tab，前面調整成 2026、2025……以此類推」); a wall
  // wider than a year is its stage word alone, 「大學」「國高中」 (哲宇 9/18:
  // 「求學那兩個也縮成『大學』『國高中』」), which is the label with its year
  // range taken off. The full "2025 到 2026" / "大學 2013 到 2016" label stays
  // on the page title, and the period is printed beside the heading. A wide
  // wall whose label is only a range keeps the range: a tab must say
  // something.
  tabText = (entry) => {
    if (
      Number(entry.period.end.slice(0, 4)) -
        Number(entry.period.start.slice(0, 4)) <=
      1
    )
      return entry.year;
    const label = localText(entry.label, lang.value),
      word = label.replace(/\s*\d{4}\s*(?:到|–|—|-)\s*\d{4}\s*/, "").trim();
    return word || label;
  };
const catalog = computed(() =>
  validateLabCatalog(props.catalog || archives.get(yearEntry.value.file)),
);
const messages = {
  zh: {
    tagline: "地層 Strata｜{from}–{to}，每一年留下來的東西",
    close: "清除選取",
    title: "一年的創作現場",
    intro: "沿著聲音、記憶與教學，走進這一年持續發生的實驗。",
    // A wall that spans several years: the school walls, filed one per stage.
    titleSpan: "求學時期的創作現場",
    introSpan: "沿著動畫、音樂與程式，走進那幾年留下的公開紀錄。",
    records: "筆公開紀錄",
    filter: "策展主題",
    all: "全部脈絡",
    index: "完整紀錄",
    legend: "紅線：來源關聯　麻線：共同主題　移到紙上看關係名稱",
    selected: "目前選取",
    open: "前往原始頁面",
    undated: "持續進行",
    theme: "策展主題",
    sourceRelation: "來源關聯",
    readTitle: "逐一閱讀",
    readNote:
      "最新的在最上面，往下越舊。每筆紀錄保留日期依據、公開來源與關係說明。",
    sources: "來源",
    related: "相關紀錄",
    showWall: "在牆上選取",
    years: "年度",
    empty: "目前沒有可展示的公開紀錄。",
  },
  en: {
    tagline: "Strata | {from}–{to}: what each year left behind",
    close: "Clear selection",
    title: "A year in the making",
    intro:
      "Art, sound, tools and teaching meet on one wall. Follow a documented connection, or begin with a single piece of paper.",
    titleSpan: "The school years in the making",
    introSpan:
      "Animation, music and code meet on one wall. Follow a documented connection, or begin with a single piece of paper.",
    records: "public records",
    filter: "Curatorial themes",
    all: "All connections",
    index: "Complete records",
    legend:
      "Red string: documented links · Twine: shared themes · Hover a paper to read a connection.",
    selected: "Selected record",
    open: "Open the original page",
    undated: "Undated",
    theme: "Curatorial theme",
    sourceRelation: "Documented connection",
    readTitle: "Read each record",
    readNote:
      "Newest first, older as you go down. Each record retains its date basis, public sources and connection labels.",
    sources: "Sources",
    related: "Related records",
    showWall: "Select on the wall",
    years: "Years",
    empty: "No public records are available to display.",
  },
};
const spanFrom = Math.min(...years.map((e) => Number(e.period.start.slice(0, 4)))),
  spanTo = Math.max(...years.map((e) => Number(e.period.end.slice(0, 4))));
const t = computed(() => ({
    ...messages[lang.value],
    tagline: messages[lang.value].tagline
      .replace("{from}", spanFrom)
      .replace("{to}", spanTo),
  })),
  // The heading says "a year" only when the window is one. The school walls
  // (2007–2013, 2013–2016) open wider than twelve months, and the title and
  // intro follow the period printed beside them rather than contradict it.
  heading = computed(() => {
    const { start, end } = catalog.value.period,
      span = Number(end.slice(0, 4)) - Number(start.slice(0, 4));
    return span > 1
      ? { title: t.value.titleSpan, intro: t.value.introSpan }
      : { title: t.value.title, intro: t.value.intro };
  }),
  visibleItems = computed(() =>
    catalog.value.items.filter(
      (item) => group.value === "all" || labGroup(item) === group.value,
    ),
  ),
  itemById = (id) => catalog.value.items.find((item) => item.id === id);
const selected = computed(() => itemById(route.query.id) || null),
  relations = (id) => labRelations(catalog.value.items, id),
  // Links into another year's wall (calendar years since 2026-09-24): the
  // title comes from that year's archive, the link opens that year with the
  // record's anchor.
  crossLinks = (item) => labCrossLinks(item),
  crossEntry = (edge) => years.find((entry) => entry.year === edge.year),
  crossTitle = (edge) =>
    localText(
      archives
        .get(crossEntry(edge)?.file)
        ?.items.find((other) => other.id === edge.id)?.title,
      lang.value,
    ) || edge.id,
  crossYear = (edge) => (crossEntry(edge) ? tabText(crossEntry(edge)) : edge.year),
  crossHref = (edge) =>
    (crossEntry(edge) ? yearPath(crossEntry(edge)) : "") + "#lab-" + edge.id,
  connections = computed(() =>
    selected.value ? relations(selected.value.id) : [],
  );
// The dossier's pages, newest first, with a year tab wherever the year
// changes between two neighbouring pages. An undated record carries no year
// and prints no tab; the first dated page after it opens its year.
const records = computed(() => {
  let year = null;
  return labNewestFirst(catalog.value.items).map((item) => {
    const itemYear = item.date ? item.date.slice(0, 4) : null,
      yearTab = itemYear && itemYear !== year ? itemYear : null;
    if (itemYear) year = itemYear;
    return { item, yearTab };
  });
});
// The paper on each page is cut by the same rules as the paper on the wall,
// so a record never changes shape between the board and the dossier.
// A cutting carries the name of the outlet that printed it, as the record's
// first source names it (the wall does the same).
const masthead = (item) =>
  ((item.sources || [])[0]?.label || "")
    .split(" — ")[0]
    .replace(/ on YouTube$/, "")
    .trim() || "CHE-YU WU · PRESS";
const paperShape = (item) => labPaperShape(item),
  fasten = (item) => labFasten(item),
  pinTone = (item) => LAB_PIN_TONES[labGroup(item)],
  // ±0.8°, decided by the id: the same page leans the same way every visit.
  tilt = (item) => ((labHash(item.id + "dossier") * 2 - 1) * 0.8).toFixed(2);
const image = (item) =>
  typeof item.image === "string" ? item.image : item.image?.src;
const external = (url) =>
  /^https?:\/\//.test(url) && !url.startsWith("https://cheyuwu.com/");
const sourceUrl = (url) => url;
// The page claims each record is readable back to its public source. This is
// the only measure of whether anyone actually walks that last step.
const trackSource = (item, url, linkKind) =>
  trackEvent("lab_source_open", {
    record_id: item.id,
    record_title: localText(item.title, "en"),
    link_url: url,
    link_kind: linkKind,
    link_target: external(url) ? "external" : "internal",
    lang: lang.value,
    year: activeYear.value,
  });
// The closed status vocabulary of public/ai/lab/schema.json, one label each.
const statuses = {
  published: ["已公開", "Published"],
  "past-event-listed": ["已公開活動紀錄", "Past event record"],
  "artist-recorded": ["藝術家紀錄", "Artist record"],
  "public-ongoing": ["公開持續進行", "Public · ongoing"],
  "public-prototype": ["公開原型", "Public prototype"],
};
const statusText = (status) =>
  statuses[status]?.[lang.value === "zh" ? 0 : 1] || status;
// The closed kind vocabulary of the same schema, stamped on each paper's
// caption band: the one word that says what sort of record this page is.
const kinds = {
  prototype: ["原型", "Prototype"],
  publication: ["文章", "Writing"],
  "open-source": ["開源", "Open source"],
  exhibition: ["展覽", "Exhibition"],
  talk: ["演講", "Talk"],
  press: ["報導", "Press"],
  course: ["課程", "Course"],
  music: ["音樂", "Music"],
  podcast: ["節目", "Podcast"],
  nft: ["NFT", "NFT"],
  workshop: ["工作坊", "Workshop"],
  performance: ["演出", "Performance"],
  interview: ["訪談", "Interview"],
  award: ["獎項", "Award"],
  "ongoing-practice": ["持續實驗", "Ongoing practice"],
};
const kindText = (kind) =>
  kinds[kind]?.[lang.value === "zh" ? 0 : 1] || kind;
async function select(id, fromIndex = false, surface = "wall") {
  const item = itemById(id);
  if (!item) return;
  // Which record pulls attention, and whether people arrive at it from the
  // wall, the written index or a documented connection between two records.
  trackEvent("lab_record_select", {
    record_id: id,
    record_title: localText(item.title, "en"),
    record_status: item.status,
    surface,
    lang: lang.value,
    year: activeYear.value,
  });
  group.value = "all";
  await router.push({
    path: route.path,
    query: { ...route.query, id },
    hash: route.hash,
  });
  await nextTick();
  const detail = document.querySelector(".lab-selected");
  if (fromIndex && innerWidth > 800) {
    document.querySelector(".lab-stage")?.scrollIntoView({ block: "start" });
    detail?.focus({ preventScroll: true });
  }
  if (innerWidth <= 800) {
    detail?.scrollIntoView({ block: "nearest" });
    detail?.focus({ preventScroll: true });
  }
}
async function clearSelection() {
  const id = selected.value?.id;
  const query = { ...route.query };
  delete query.id;
  await router.push({ path: route.path, query, hash: route.hash });
  await nextTick();
  document
    .querySelector(`[data-lab-id="${id}"]`)
    ?.focus({ preventScroll: true });
}
watch(
  () => route.query.id,
  (id) => {
    if (id && itemById(id)) group.value = "all";
  },
);
// The citable form of a record is the year page plus #lab-{id}, the fragment
// of its <article> below, which the prerendered HTML resolves on its own.
// Once the app is up, that fragment also selects the record, so the panel and
// the anchor agree; ?id= keeps working as before and wins when both are given.
onMounted(() => {
  const anchored = /^#lab-([a-z0-9][a-z0-9-]*)$/.exec(location.hash || "");
  if (!anchored || route.query.id || !itemById(anchored[1])) return;
  router.replace({
    path: route.path,
    query: { ...route.query, id: anchored[1] },
    hash: route.hash,
  });
});
</script>
<style scoped>
/* Handwriting for dates and English marginalia, typewriter for memo cards.
   Loaded on the Lab page only. */
@import url("https://fonts.googleapis.com/css2?family=Caveat:wght@500&family=IBM+Plex+Mono:wght@400&display=swap");
.lab-reading-links {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  margin-top: 12px;
}
.lab-reading-links a {
  color: #cfbd8b;
  font-size: 12px;
}
.lab-heading {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  align-items: center;
}
.lab-heading > .lab-eyebrow {
  grid-column: 1/-1;
  margin-bottom: 8px;
}
.lab-heading > div {
  display: block !important;
}
.lab-heading span {
  display: block;
  margin-top: 8px;
}
.lab-heading h1 {
  margin: 0 !important;
}
@media (max-width: 800px) {
  .lab-heading {
    display: block;
  }
  .lab-heading h1 {
    margin-bottom: 15px !important;
  }
}
/* Case folder tabs: kraft card, squared off where they meet the board, the
   year being read raised and lit. */
.lab-years {
  grid-column: 1 / -1;
  justify-self: end;
  display: flex;
  /* Twelve tabs since the school-year walls (2026-09-18): a row that no
     longer fits folds onto a second row instead of running off the board. */
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 26px;
}
.lab-year-tab {
  display: inline-block;
  padding: 9px 16px 8px;
  min-height: 38px;
  background: #3a2d1e;
  color: #d8c9a4;
  border: 0;
  border-radius: var(--r-sm) var(--r-sm) 0 0;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-decoration: none;
}
.lab-year-tab.current {
  background: #c9a86a;
  color: #22190e;
}
@media (max-width: 800px) {
  .lab-years {
    justify-self: start;
    margin-top: 18px;
    flex-wrap: wrap;
  }
}
.lab-stage {
  position: relative;
}
.lab-stage.has-selection {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
  align-items: start;
}
.lab-stage .lab-selected {
  display: block;
  padding: 28px 0;
  margin-top: 25px;
  max-height: 740px;
  overflow: auto;
}
.lab-stage .lab-selected h2 {
  font-size: 26px;
}
.lab-stage .lab-selected aside {
  margin-top: 22px;
}
@media (max-width: 800px) {
  .lab-stage.has-selection {
    display: block;
  }
  .lab-stage .lab-selected {
    max-height: none;
  }
}
.page-lab {
  max-width: 1680px;
  margin: auto;
  padding: 28px 4vw 100px;
  color: #ece7dc;
  background: #0a0b0b;
  font-family: var(--font-body);
  text-align: left;
  overflow-wrap: anywhere;
}
.lab-eyebrow {
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  color: #b5a887;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 20px;
}
.page-lab .lab-heading p.lab-tagline {
  font-size: clamp(18px, 1.7vw, 22px);
  line-height: 1.6;
  margin: 0 0 18px;
  color: #e9cf8d;
  max-width: none;
}
.lab-heading h1 {
  font-family: var(--font-slab), var(--font-display);
  font-size: clamp(40px, 4.5vw, 64px);
  font-weight: 400;
  margin: 0 0 16px;
  line-height: 1.12;
}
.page-lab:lang(zh-Hant) h1 {
  font-family: var(--font-display);
  font-size: clamp(38px, 4vw, 56px);
}
.lab-heading > div {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 30px;
}
.lab-heading p:not(.lab-eyebrow) {
  font-size: 17px;
  line-height: 1.8;
  max-width: 58ch;
  margin: 0;
}
.lab-heading span {
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.9;
  color: #aaa28f;
  white-space: nowrap;
}
.lab-select-record {
  background: var(--tint-2);
  border: 0;
  border-radius: var(--r-pill);
  color: #e7dfca;
  font: inherit;
  font-size: 13px;
  padding: 10px 18px;
  cursor: pointer;
  min-height: 40px;
  text-decoration: none;
  transition: var(--t-hover);
}
.lab-select-record:hover {
  background: var(--tint-3);
}
.lab-legend {
  font-size: 11px;
  color: #a9a28f;
  line-height: 1.7;
}
.lab-selected {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 60px;
  padding: 35px 0 50px;
  border-top: 1px solid #8e8060;
}
.lab-selected h2,
.lab-records h2 {
  font-family: var(--font-display);
  font-size: 34px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0 0 20px;
}
.lab-selected p {
  line-height: 1.9;
}
.lab-selected aside {
  font-size: 13px;
  color: #b9b09a;
}
.lab-selected ul {
  padding: 0;
  list-style: none;
}
.lab-selected li {
  margin: 20px 0;
}
.lab-selected li button {
  background: none;
  border: 0;
  border-bottom: 1px solid #78694e;
  font: inherit;
  color: #e0c993;
  padding: 7px 0;
  cursor: pointer;
  text-align: left;
}
.lab-selected li span {
  display: block;
  margin-top: 7px;
}
.lab-selected a,
.lab-record a {
  color: #dac58e;
  text-underline-offset: 4px;
}
.lab-records {
  padding-top: 35px;
  border-top: 1px solid #5b5547;
  scroll-margin-top: 100px;
}
.lab-records > header {
  margin-bottom: 45px;
}
.lab-records > header > p:last-child {
  color: #afa994;
  line-height: 1.8;
}
/* A year tab between two pages, the same kraft card as the folder tabs. */
.lab-year-mark {
  display: inline-block;
  margin: 30px 0 -6px;
  padding: 7px 14px 6px;
  background: #c9a86a;
  color: #22190e;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 12px;
  letter-spacing: 0.14em;
  transform: rotate(-0.8deg);
}
/* One page of the dossier: the paper on the left, the report on the right.
   The paper column is capped so a photograph reads as a pinned print, not a
   hero image; inside it the picture is whole, at its own proportions. */
.lab-record {
  display: grid;
  grid-template-columns: clamp(260px, 34%, 440px) minmax(0, 1fr);
  gap: 48px;
  padding: 36px 0;
  border-top: 1px solid #35392f;
  scroll-margin-top: 100px;
  align-items: start;
}
.lab-record.selected .lab-record-paper {
  outline: 3px solid #f0c765;
  outline-offset: 4px;
}
.lab-record.selected h3 {
  color: #e9cf8d;
}
/* ----- The paper ----- */
.lab-record-paper {
  position: relative;
  margin: 10px 0 0;
  padding: 12px 12px 10px;
  background-color: var(--sheet, #fbf7ee);
  color: #22251c;
  box-shadow: 7px 9px 16px rgba(14, 7, 2, 0.5);
  transform: rotate(var(--tilt, 0deg));
  overflow: visible;
}
/* A page without a photograph is a card, not a banner. */
.lab-record-paper:not(.has-image) {
  max-width: 300px;
  min-height: 118px;
}
.lab-record-paper.shape-tag:not(.has-image) {
  max-width: 250px;
}
.lab-record-paper img {
  display: block;
  width: 100%;
  height: auto;
  background: #17140f;
}
.lab-record-paper figcaption {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  gap: 2px 12px;
  padding: 10px 2px 2px;
}
.lab-record-paper figcaption time,
.lab-record-paper figcaption > span:first-child {
  font-family: "Caveat", var(--font-display), cursive;
  font-size: 25px;
  line-height: 1.1;
  color: #6a4f32;
}
/* The kind, stamped: one word, slightly off square, no box. */
.lab-record-kind {
  justify-self: end;
  padding: 3px 8px 2px;
  border: 0;
  border-radius: var(--r-sm);
  background: rgba(78, 69, 54, 0.1);
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #4e4536;
  transform: rotate(-1.5deg);
}
.lab-record-paper figcaption small {
  grid-column: 1 / -1;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 11px;
  line-height: 1.5;
  color: #6b6352;
}
/* Ten kinds of paper, the same colours as the wall (LabWall.vue, space and
   flat modes). Change one, change all three. */
.lab-record-paper.shape-exhibit,
.lab-record-paper.shape-polaroid {
  --sheet: #fbf7ee;
  padding-bottom: 14px;
  border: 1px solid rgba(120, 101, 72, 0.25);
}
.lab-record-paper.shape-capture {
  --sheet: #fcfbf7;
  padding: 6px 6px 8px;
  border: 1px solid rgba(120, 101, 72, 0.3);
}
.lab-record-paper.shape-clipping {
  --sheet: #eee2c4;
  padding-top: 36px;
  border-top: 7px solid #23201a;
}
.lab-record-paper.shape-note {
  --sheet: #e3ebef;
  padding-top: 18px;
  border-left: 4px solid #bf5a4b;
}
.lab-record-paper.shape-tag {
  --sheet: #cbaf82;
  padding-top: 38px;
  border: 1px solid rgba(96, 70, 36, 0.5);
  background-image: linear-gradient(
    118deg,
    rgba(255, 255, 255, 0.14),
    rgba(90, 64, 30, 0.12)
  );
}
/* An index card, line-printer paper, music paper and a ticket stub: the
   record with no picture, filed as the kind of evidence it is, one cue each
   (a red rule, feed holes, a double rule, a perforation) and no ruled lines
   standing in for words. */
.lab-record-paper.shape-card {
  --sheet: #fbf8ef;
  padding-top: 30px;
  border: 1px solid rgba(120, 101, 72, 0.22);
  background-image: linear-gradient(
    180deg,
    transparent 22px,
    rgba(196, 64, 52, 0.72) 22px 23.5px,
    transparent 23.5px
  );
}
.lab-record-paper.shape-printout {
  --sheet: #f3f1e7;
  padding-left: 30px;
  padding-right: 30px;
  background-image:
    radial-gradient(circle, #5b3f25 0 3.5px, transparent 4px) 6px 10px / 12px 22px
      repeat-y,
    radial-gradient(circle, #5b3f25 0 3.5px, transparent 4px) calc(100% - 6px)
      10px / 12px 22px repeat-y;
}
.lab-record-paper.shape-score {
  --sheet: #f7f1e1;
  border: 1px solid rgba(120, 101, 72, 0.2);
  border-top: 3px double rgba(70, 58, 44, 0.45);
}
.lab-record-paper.shape-ticket {
  --sheet: #e8d6a6;
  padding-right: 54px;
  border-right: 2px dotted rgba(90, 60, 25, 0.5);
  background-image: linear-gradient(
    115deg,
    rgba(255, 255, 255, 0.2),
    rgba(120, 80, 30, 0.1)
  );
}
.lab-record-sheet {
  display: block;
  margin: 2px 2px 8px;
  max-height: 200px;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(180deg, #000 68%, transparent);
  mask-image: linear-gradient(180deg, #000 68%, transparent);
}
.lab-record-sheet::before {
  content: attr(data-title);
  display: block;
  margin-bottom: 8px;
  font-family: var(--font-display);
  font-size: 19px;
  line-height: 1.32;
  color: #22251c;
}
.lab-record-sheet::after {
  content: attr(data-lede);
  display: block;
  font-size: 12.5px;
  line-height: 1.62;
  color: #4a3f30;
}
.lab-record-paper.shape-clipping .lab-record-sheet::after {
  column-count: 2;
  column-gap: 16px;
  text-align: justify;
}
.lab-record-paper.shape-printout .lab-record-sheet::before,
.lab-record-paper.shape-printout .lab-record-sheet::after {
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  letter-spacing: -0.02em;
}
.lab-record-paper.shape-printout .lab-record-sheet::after {
  font-size: 11.5px;
  color: #3f5a3f;
}
.lab-record-masthead {
  position: absolute;
  top: 10px;
  left: 12px;
  right: 12px;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 9px;
  font-weight: 400;
  letter-spacing: 0.22em;
  color: #23201a;
  border-bottom: 2px solid #23201a;
  padding-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
}
.lab-record-eyelet {
  position: absolute;
  top: 10px;
  left: 50%;
  width: 14px;
  height: 14px;
  margin-left: -7px;
  border-radius: 50%;
  background: #1c1205;
  box-shadow: 0 0 0 2px rgba(255, 250, 236, 0.55);
}
.lab-record-eyelet::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 90%;
  width: 2px;
  height: 34px;
  background: repeating-linear-gradient(
    -28deg,
    #d9c79e 0 3px,
    #a98f5e 3px 6px
  );
  transform-origin: 50% 100%;
  transform: rotate(16deg);
}
/* Pinned or taped, as on the wall; the pin head takes the record's theme
   colour, the same four as the pins on the board. */
.lab-record-pin {
  position: absolute;
  top: -7px;
  left: 50%;
  width: 14px;
  height: 14px;
  margin-left: -7px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 33% 28%,
    rgba(255, 255, 255, 0.85),
    var(--pin, #a5241d) 62%
  );
  box-shadow: 1px 2px 3px rgba(8, 4, 1, 0.6);
}
/* A tag hangs by its twine; a pin through the eyelet would be two fastenings. */
.lab-record-paper.shape-tag .lab-record-pin {
  display: none;
}
.lab-record-tape {
  position: absolute;
  top: -10px;
  width: 26%;
  height: 20px;
  background: linear-gradient(
    180deg,
    rgba(240, 224, 176, 0.72),
    rgba(214, 193, 143, 0.62)
  );
  box-shadow: 0 1px 3px rgba(20, 11, 3, 0.35);
}
.lab-record-tape.left {
  left: -6%;
  transform: rotate(-38deg);
}
.lab-record-tape.right {
  right: -6%;
  transform: rotate(38deg);
}
/* ----- The report ----- */
.lab-record-meta {
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.8;
  color: #ada690;
  margin: 0 0 14px;
}
.lab-record-meta small {
  display: block;
  font-size: 11px;
  color: #8f886f;
}
.lab-record h3 {
  font-family: var(--font-display);
  font-size: 27px;
  font-weight: 400;
  margin: 0 0 18px;
  line-height: 1.6;
}
.lab-record-report > p:not(.lab-record-meta) {
  line-height: 1.9;
  font-size: 16px;
  max-width: 72ch;
  margin: 0 0 16px;
}
.lab-keywords {
  display: flex;
  gap: 10px 20px;
  flex-wrap: wrap;
  padding: 0;
  margin: 0 0 22px;
  list-style: none;
  color: #c5b47f;
  font-size: 12px;
}
.lab-record-links {
  margin: 0 0 22px;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.9;
  color: #b8b29e;
}
.lab-record-links dt {
  color: #8f886f;
  letter-spacing: 0.08em;
  margin: 8px 0 2px;
}
.lab-record-links dt:first-child {
  margin-top: 0;
}
.lab-record-links dd {
  margin: 0;
}
.page-lab button:focus-visible,
.page-lab a:focus-visible,
.page-lab summary:focus-visible {
  outline: 2px solid #e4c57e;
  outline-offset: 4px;
}
@media (max-width: 800px) {
  .page-lab {
    padding: 38px 22px 70px;
  }
  .lab-heading > div {
    display: block;
  }
  .lab-heading span {
    display: block;
    margin-top: 18px;
  }
  .lab-selected {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  /* One column: the paper on top, the report under it, the photograph as
     wide as the page. */
  .lab-record {
    grid-template-columns: 1fr;
    gap: 26px;
    padding: 30px 0;
  }
  .lab-record-paper {
    margin: 12px 6px 0 0;
  }
  .lab-record h3 {
    font-size: 24px;
  }
}
.lab-lineage-teaser{margin:34px 0 40px}
</style>
